import { UPGRADE_FUNNEL_BROWSER_EVENT } from '@shared/core/analytics'
import type { BillingPlanDto } from '@shared/core/billing'
import { useCallback, useEffect, useRef } from 'react'
import { type TrackUpgradeFunnelEvent, useTrackUpgradeFunnel } from 'src/modules/payment/hooks/use-track-upgrade-funnel'
import type { PlansAccount, PlansFunnelContext } from 'src/modules/payment/types/plans-offer.types'
import {
  PADDLE_CHECKOUT_LOADED_EVENT,
  readCheckoutPlanId,
  readCheckoutTrigger,
  subscribeToPaddleCheckoutEvents,
} from 'src/modules/payment/utils/paddle-checkout-events'
import { getPlanBillingInterval } from 'src/modules/payment/utils/plans-offer.utils'

export type UsePlansFunnelTrackingOptions = PlansFunnelContext & {
  /** `null` until the account state is known; nothing is recorded before that. */
  account: PlansAccount | null
  /** Every plan the surface offers, both intervals. */
  plans: BillingPlanDto[]
  recommendedPlanId?: number
}

/** Records `paywall_viewed` once per mount and `checkout_started` on Paddle's load event; returns the press tracker. */
export function usePlansFunnelTracking(options: UsePlansFunnelTrackingOptions) {
  const { surface, params, account, recommendedPlanId } = options
  const track = useTrackUpgradeFunnel()

  useTrackPaywallViewedOnce(options, track)
  useTrackCheckoutStarted(options, track)

  return useCallback(
    (plan: BillingPlanDto) => {
      if (!account) {
        return
      }

      track(UPGRADE_FUNNEL_BROWSER_EVENT.UPGRADE_CLICKED, {
        surface,
        plan_id: plan.paddle_id,
        billing_interval: getPlanBillingInterval(plan),
        is_recommended: plan.paddle_id === recommendedPlanId,
        account_state: account.accountState,
        action: account.isFreeTrial ? 'new_checkout' : 'plan_change',
        trigger: params.trigger,
      })
    },
    [account, params.trigger, recommendedPlanId, surface, track],
  )
}

function useTrackPaywallViewedOnce(
  { surface, params, account }: UsePlansFunnelTrackingOptions,
  track: TrackUpgradeFunnelEvent,
) {
  const hasTracked = useRef(false)

  useEffect(() => {
    if (!account || hasTracked.current) {
      return
    }

    hasTracked.current = true
    track(UPGRADE_FUNNEL_BROWSER_EVENT.PAYWALL_VIEWED, {
      surface,
      display: 'user_click',
      account_state: account.accountState,
      trigger: params.trigger,
      source_element: params.source_element,
      source_page: params.source_page,
    })
  }, [account, params, surface, track])
}

function useTrackCheckoutStarted({ surface, plans }: UsePlansFunnelTrackingOptions, track: TrackUpgradeFunnelEvent) {
  const plansRef = useRef(plans)
  plansRef.current = plans

  useEffect(
    () =>
      subscribeToPaddleCheckoutEvents((event) => {
        const planId = readCheckoutPlanId(event)
        const plan = plansRef.current.find((candidate) => candidate.paddle_id === planId)

        if (event?.event !== PADDLE_CHECKOUT_LOADED_EVENT || !plan) {
          return
        }

        track(UPGRADE_FUNNEL_BROWSER_EVENT.CHECKOUT_STARTED, {
          surface,
          plan_id: planId,
          billing_interval: getPlanBillingInterval(plan),
          trigger: readCheckoutTrigger(event),
        })
      }),
    [surface, track],
  )
}
